/**
 * Macro signals panel — aggregates macro economic indicators into
 * a combined risk-on/risk-off sentiment indicator.
 *
 * Signals tracked:
 * - Yield curve (10Y-2Y spread: inverted = risk-off)
 * - VIX proxy (market volatility via change magnitude)
 * - DXY proxy (USD strength via USD/EUR rate)
 * - Gold/Oil ratio (safe-haven demand)
 * - Overall risk-on / risk-off reading
 *
 * Data sourced from FRED events + market-source quotes in the store.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { getLatestMarketQuotes } from '@/sources/built-in/market-source';
import { createSparkline } from '@/ui/components/sparkline';
import { getApiBase } from '@/app/api-router';

type Regime = 'risk-on' | 'neutral' | 'risk-off';

interface MacroSignal {
  name: string;
  value: number | null;
  unit: string;
  signal: Regime;
  description: string;
}

/** History for sparklines (session-local). */
const signalHistory = new Map<string, number[]>();
const MAX_HISTORY = 20;

function recordHistory(key: string, value: number): void {
  let history = signalHistory.get(key);
  if (!history) {
    history = [];
    signalHistory.set(key, history);
  }
  if (history.length === 0 || history[history.length - 1] !== value) {
    history.push(value);
    if (history.length > MAX_HISTORY) history.shift();
  }
}

interface FearGreedData {
  score: number;
  label: string;
  history: number[];
  updatedAt: string;
}

let fearGreedCache: FearGreedData | null = null;
let fearGreedLastFetch = 0;
const FEAR_GREED_TTL = 5 * 60 * 1000;

async function fetchFearGreed(): Promise<FearGreedData | null> {
  if (fearGreedCache && Date.now() - fearGreedLastFetch < FEAR_GREED_TTL) {
    return fearGreedCache;
  }

  try {
    const url = `${getApiBase()}/api/data/fear-greed`;
    const resp = await fetch(url, { signal: AbortSignal.timeout(10_000) });
    if (!resp.ok) return fearGreedCache;

    const data = await resp.json();
    const fgi = data?.fear_and_greed;
    if (!fgi) return fearGreedCache;

    const score = Math.round(fgi.score ?? 50);
    const label = String(fgi.rating ?? 'Neutral');

    const historyPoints: number[] = [];
    const timeline = data?.fear_and_greed_historical?.data;
    if (Array.isArray(timeline)) {
      for (const pt of timeline.slice(-30)) {
        if (typeof pt.x === 'number' && typeof pt.y === 'number') {
          historyPoints.push(Math.round(pt.y));
        }
      }
    }

    fearGreedCache = {
      score,
      label,
      history: historyPoints,
      updatedAt: new Date().toISOString(),
    };
    fearGreedLastFetch = Date.now();
    return fearGreedCache;
  } catch {
    return fearGreedCache;
  }
}

function fearGreedColor(score: number): string {
  if (score <= 25) return 'var(--accent-red)';
  if (score <= 45) return 'var(--accent-orange, #f59e0b)';
  if (score <= 55) return 'var(--text-muted)';
  if (score <= 75) return 'var(--accent-green)';
  return '#22c55e';
}

export function createMacroSignalsPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'macro-signals-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function computeSignals(): MacroSignal[] {
    const events = appStore.getState().events;
    const quotes = getLatestMarketQuotes();
    const signals: MacroSignal[] = [];

    // 1. Yield Curve (from FRED T10Y2Y)
    const spreadEvent = events.find(e => {
      const raw = e.raw as { seriesId?: string } | undefined;
      return e.source === 'fred' && raw?.seriesId === 'T10Y2Y';
    });
    const spreadValue = spreadEvent ? (spreadEvent.raw as { value?: number })?.value ?? null : null;
    if (spreadValue !== null) recordHistory('yield-curve', spreadValue);
    signals.push({
      name: 'Yield Curve (10Y-2Y)',
      value: spreadValue,
      unit: '%',
      signal: spreadValue === null ? 'neutral' : spreadValue < 0 ? 'risk-off' : spreadValue < 0.5 ? 'neutral' : 'risk-on',
      description: spreadValue !== null && spreadValue < 0 ? 'Inverted — recession signal' : 'Normal',
    });

    // 2. Volatility proxy (average absolute change across stock indices)
    const indexQuotes = quotes.filter(q => q.category === 'index');
    const avgVolatility = indexQuotes.length > 0
      ? indexQuotes.reduce((sum, q) => sum + Math.abs(q.change24h), 0) / indexQuotes.length
      : null;
    if (avgVolatility !== null) recordHistory('volatility', avgVolatility);
    signals.push({
      name: 'Market Volatility',
      value: avgVolatility !== null ? Math.round(avgVolatility * 100) / 100 : null,
      unit: '%',
      signal: avgVolatility === null ? 'neutral' : avgVolatility > 3 ? 'risk-off' : avgVolatility > 1.5 ? 'neutral' : 'risk-on',
      description: avgVolatility !== null && avgVolatility > 3 ? 'Elevated volatility' : 'Normal range',
    });

    // 3. USD Strength (from FRED DEXUSEU — lower = stronger dollar)
    const usdEvent = events.find(e => {
      const raw = e.raw as { seriesId?: string } | undefined;
      return e.source === 'fred' && raw?.seriesId === 'DEXUSEU';
    });
    const usdEur = usdEvent ? (usdEvent.raw as { value?: number })?.value ?? null : null;
    if (usdEur !== null) recordHistory('usd-strength', usdEur);
    signals.push({
      name: 'USD Strength (USD/EUR)',
      value: usdEur,
      unit: '',
      signal: usdEur === null ? 'neutral' : usdEur < 1.05 ? 'risk-off' : usdEur > 1.15 ? 'risk-on' : 'neutral',
      description: usdEur !== null && usdEur < 1.05 ? 'Strong USD — flight to safety' : 'Normal range',
    });

    // 4. Gold (from market quotes)
    const goldQuote = quotes.find(q => q.symbol === 'GLD');
    const goldChange = goldQuote?.change24h ?? null;
    if (goldChange !== null) recordHistory('gold', goldChange);
    signals.push({
      name: 'Gold (GLD)',
      value: goldChange !== null ? Math.round(goldChange * 100) / 100 : null,
      unit: '% 24h',
      signal: goldChange === null ? 'neutral' : goldChange > 2 ? 'risk-off' : goldChange < -1 ? 'risk-on' : 'neutral',
      description: goldChange !== null && goldChange > 2 ? 'Safe-haven buying' : 'Normal',
    });

    // 5. Equity momentum (S&P 500 change)
    const spyQuote = quotes.find(q => q.symbol === 'SPY');
    const spyChange = spyQuote?.change24h ?? null;
    if (spyChange !== null) recordHistory('equity-momentum', spyChange);
    signals.push({
      name: 'S&P 500 Momentum',
      value: spyChange !== null ? Math.round(spyChange * 100) / 100 : null,
      unit: '% 24h',
      signal: spyChange === null ? 'neutral' : spyChange > 1 ? 'risk-on' : spyChange < -2 ? 'risk-off' : 'neutral',
      description: spyChange !== null && spyChange < -2 ? 'Sell-off in progress' : 'Normal',
    });

    return signals;
  }

  function computeOverallRegime(signals: MacroSignal[]): { regime: Regime; score: number } {
    let riskOnCount = 0;
    let riskOffCount = 0;
    let total = 0;

    for (const s of signals) {
      if (s.value === null) continue;
      total++;
      if (s.signal === 'risk-on') riskOnCount++;
      if (s.signal === 'risk-off') riskOffCount++;
    }

    if (total === 0) return { regime: 'neutral', score: 50 };

    const score = Math.round(((riskOnCount - riskOffCount) / total + 1) * 50);
    const regime: Regime = score > 65 ? 'risk-on' : score < 35 ? 'risk-off' : 'neutral';
    return { regime, score };
  }

  function render(): void {
    contentArea.textContent = '';

    const signals = computeSignals();
    const { regime, score } = computeOverallRegime(signals);

    // Overall regime indicator
    const regimeCard = document.createElement('div');
    const regimeColor = regime === 'risk-on' ? 'var(--accent-green)' : regime === 'risk-off' ? 'var(--accent-red)' : 'var(--accent-blue)';
    regimeCard.style.cssText = `
      padding: 12px; border-radius: 6px; margin-bottom: 12px;
      background: var(--bg-secondary); border: 2px solid ${regimeColor};
      text-align: center;
    `;

    const regimeLabel = document.createElement('div');
    regimeLabel.style.cssText = `font-size: 14px; font-weight: 700; color: ${regimeColor}; text-transform: uppercase;`;
    regimeLabel.textContent = regime.replace('-', ' ');
    regimeCard.appendChild(regimeLabel);

    // Score bar
    const barContainer = document.createElement('div');
    barContainer.style.cssText = 'margin-top: 8px; height: 8px; border-radius: 4px; background: var(--bg-primary); position: relative;';

    const barFill = document.createElement('div');
    barFill.style.cssText = `
      height: 100%; border-radius: 4px; transition: width 300ms ease;
      width: ${score}%; background: ${regimeColor};
    `;
    barContainer.appendChild(barFill);
    regimeCard.appendChild(barContainer);

    const barLabels = document.createElement('div');
    barLabels.style.cssText = 'display: flex; justify-content: space-between; margin-top: 4px; font-size: 9px; color: var(--text-muted);';
    const leftLabel = document.createElement('span');
    leftLabel.textContent = 'Risk-Off';
    const rightLabel = document.createElement('span');
    rightLabel.textContent = 'Risk-On';
    barLabels.appendChild(leftLabel);
    barLabels.appendChild(rightLabel);
    regimeCard.appendChild(barLabels);

    contentArea.appendChild(regimeCard);

    // Fear & Greed gauge
    renderFearGreedGauge(contentArea);

    const signalHeader = document.createElement('div');
    signalHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;';
    signalHeader.textContent = 'Signal Components';
    contentArea.appendChild(signalHeader);

    if (signals.every(s => s.value === null)) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'Waiting for market and economic data\u2026';
      contentArea.appendChild(msg);
      return;
    }

    for (const sig of signals) {
      const row = document.createElement('div');
      const sigColor = sig.signal === 'risk-on' ? 'var(--accent-green)' : sig.signal === 'risk-off' ? 'var(--accent-red)' : 'var(--text-muted)';
      row.style.cssText = `
        display: flex; align-items: center; gap: 6px;
        padding: 6px 8px; margin-bottom: 3px; border-radius: 4px;
        background: var(--bg-secondary); border-left: 3px solid ${sigColor};
      `;

      // Name
      const nameEl = document.createElement('div');
      nameEl.style.cssText = 'flex: 1; min-width: 0;';
      const nameText = document.createElement('div');
      nameText.style.cssText = 'font-size: 11px; font-weight: 500; color: var(--text-primary);';
      nameText.textContent = sig.name;
      nameEl.appendChild(nameText);

      const descText = document.createElement('div');
      descText.style.cssText = 'font-size: 9px; color: var(--text-muted);';
      descText.textContent = sig.description;
      nameEl.appendChild(descText);
      row.appendChild(nameEl);

      // Sparkline
      const historyKey = sig.name.toLowerCase().replace(/[^a-z]/g, '-');
      const history = signalHistory.get(historyKey);
      if (history && history.length >= 2) {
        row.appendChild(createSparkline({ data: history, width: 40, height: 14, color: sigColor }));
      }

      // Value
      const valueEl = document.createElement('div');
      valueEl.style.cssText = `font-size: 12px; font-weight: 600; font-family: var(--font-mono); color: ${sigColor}; flex-shrink: 0;`;
      valueEl.textContent = sig.value !== null ? `${sig.value}${sig.unit ? ' ' + sig.unit : ''}` : '—';
      row.appendChild(valueEl);

      contentArea.appendChild(row);
    }
  }

  function renderFearGreedGauge(parent: HTMLElement): void {
    if (!fearGreedCache) return;

    const { score, label, history } = fearGreedCache;
    const color = fearGreedColor(score);

    const card = document.createElement('div');
    card.style.cssText = `
      padding: 10px 12px; border-radius: 6px; margin-bottom: 12px;
      background: var(--bg-secondary); border-left: 3px solid ${color};
      display: flex; align-items: center; gap: 10px;
    `;

    const gaugeWrap = document.createElement('div');
    gaugeWrap.style.cssText = 'flex-shrink: 0; text-align: center; width: 48px;';

    const scoreEl = document.createElement('div');
    scoreEl.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${color};`;
    scoreEl.textContent = String(score);
    gaugeWrap.appendChild(scoreEl);

    const labelEl = document.createElement('div');
    labelEl.style.cssText = 'font-size: 8px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.03em;';
    labelEl.textContent = label;
    gaugeWrap.appendChild(labelEl);

    card.appendChild(gaugeWrap);

    const infoWrap = document.createElement('div');
    infoWrap.style.cssText = 'flex: 1; min-width: 0;';

    const title = document.createElement('div');
    title.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-primary); margin-bottom: 4px;';
    title.textContent = 'CNN Fear & Greed';
    infoWrap.appendChild(title);

    if (history.length >= 2) {
      infoWrap.appendChild(createSparkline({ data: history, width: 80, height: 20, color }));
    }

    // Score bar (0=extreme fear, 100=extreme greed)
    const bar = document.createElement('div');
    bar.style.cssText = 'height: 4px; border-radius: 2px; background: var(--bg-primary); margin-top: 4px;';
    const fill = document.createElement('div');
    fill.style.cssText = `height: 100%; border-radius: 2px; width: ${score}%; background: ${color}; transition: width 300ms ease;`;
    bar.appendChild(fill);
    infoWrap.appendChild(bar);

    const barLabels = document.createElement('div');
    barLabels.style.cssText = 'display: flex; justify-content: space-between; font-size: 8px; color: var(--text-muted); margin-top: 2px;';
    const fearLbl = document.createElement('span');
    fearLbl.textContent = 'Fear';
    const greedLbl = document.createElement('span');
    greedLbl.textContent = 'Greed';
    barLabels.appendChild(fearLbl);
    barLabels.appendChild(greedLbl);
    infoWrap.appendChild(barLabels);

    card.appendChild(infoWrap);
    parent.appendChild(card);
  }

  // Kick off Fear & Greed fetch (async, re-renders when data arrives)
  fetchFearGreed().then(() => scheduleRender());

  // Initial render
  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );

  const interval = setInterval(() => scheduleRender(), 60_000);
  cleanupFns.push(() => clearInterval(interval));

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
